import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckSquare, Circle, Sparkles, Trash2 } from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card, CardBody } from '@/components/card';
import { EmptyState } from '@/components/empty-state';
import { PageHeader } from '@/components/page-header';
import { SkeletonList } from '@/components/skeleton';
import { useToast } from '@/components/toast';
import {
  useDeleteLeadTask,
  useLeadTasks,
  useUpdateLeadTask,
  type LeadTaskStatus,
} from '@/lib/lead-tasks';
import type { LeadTaskData } from '@/types/api';

const STATUS_LABEL: Record<LeadTaskStatus, string> = {
  open: 'All open',
  overdue: 'Overdue',
  due_soon: 'Due in 24 hours',
};

export function LeadsTasks() {
  const [status, setStatus] = useState<LeadTaskStatus>('open');
  const { data: tasks, isLoading } = useLeadTasks(status);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={CheckSquare}
        title="Tasks"
        subtitle="Open follow-ups across every lead. The sidebar badge counts overdue + due in the next day."
      />

      <div className="flex flex-wrap items-center gap-2">
        {(['open', 'overdue', 'due_soon'] as LeadTaskStatus[]).map((s) => (
          <Button
            key={s}
            variant={status === s ? 'primary' : 'secondary'}
            onClick={() => setStatus(s)}
          >
            {STATUS_LABEL[s]}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <Card>
          <CardBody>
            <SkeletonList rows={4} />
          </CardBody>
        </Card>
      ) : !tasks || tasks.length === 0 ? (
        <EmptyState
          icon={CheckSquare}
          title={
            status === 'overdue'
              ? 'Nothing overdue'
              : status === 'due_soon'
                ? 'Nothing due in the next day'
                : 'No open tasks'
          }
          description={
            status === 'open'
              ? 'Add a follow-up from a lead to see it land here.'
              : 'Filter back to "All open" to see everything.'
          }
          action={
            <Link to="/leads">
              <Button variant="secondary">
                <Sparkles className="size-4" /> Go to leads
              </Button>
            </Link>
          }
        />
      ) : (
        <Card>
          <CardBody>
            <ul className="space-y-2">
              {tasks.map((t) => (
                <TaskRow key={t.id} task={t} />
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function TaskRow({ task }: { task: LeadTaskData }) {
  const update = useUpdateLeadTask(task.lead_id);
  const remove = useDeleteLeadTask(task.lead_id);
  const toast = useToast();

  async function onComplete() {
    try {
      await update.mutateAsync({ taskId: task.id, patch: { completed: true } });
      toast.success('Task completed');
    } catch {
      toast.error("Couldn't update task");
    }
  }

  async function onDelete() {
    await remove.mutateAsync(task.id);
    toast.success('Task removed');
  }

  return (
    <li className="group/t flex items-start gap-3 rounded-md border border-border/70 px-3 py-2.5">
      <button
        type="button"
        onClick={onComplete}
        aria-label="Mark complete"
        className="mt-0.5 text-muted-foreground transition-colors hover:text-emerald-600"
      >
        <Circle className="size-4" />
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2 text-sm">
          <Link to={`/leads/${task.lead_id}`} className="font-medium hover:underline">
            {task.title}
          </Link>
          {task.is_overdue ? (
            <Badge tone="danger">Overdue</Badge>
          ) : (
            <Badge tone="neutral">Due {new Date(task.due_at).toLocaleString()}</Badge>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          {task.lead_name ? (
            <Link to={`/leads/${task.lead_id}`} className="hover:underline">
              {task.lead_name}
            </Link>
          ) : null}
          {task.assigned_to_name ? <span>Assigned to {task.assigned_to_name}</span> : null}
        </div>
        {task.notes ? <p className="mt-1 text-xs text-muted-foreground">{task.notes}</p> : null}
      </div>
      <button
        type="button"
        onClick={onDelete}
        aria-label="Delete task"
        className="text-muted-foreground opacity-0 transition-opacity group-hover/t:opacity-100 hover:text-destructive"
      >
        <Trash2 className="size-3.5" />
      </button>
    </li>
  );
}
